import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, Search, Mail, Building, User } from 'lucide-react';
import { toast } from 'sonner';

export default function UserManagementTable() {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const togglePartnerAccess = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'ACCESS_GRANTED' ? null : 'ACCESS_GRANTED';
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/partner`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ partner: newStatus })
      });
      
      if (!response.ok) throw new Error('Failed to update user');
      
      toast.success(newStatus ? 'Partner access granted' : 'Partner access removed');
      fetchUsers();
    } catch (error) {
      toast.error(error.message || 'Failed to update user');
    }
  };

  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <p className="text-gray-600">Loading users...</p>;
  }

  return (
    <div>
      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <Input
            placeholder="Search users by email, name, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Users List */}
      <div className="space-y-4">
        {filteredUsers.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No users found</p>
        ) : (
          filteredUsers.map((user) => (
            <Card key={user.id} className="border border-gray-200 hover:shadow-md transition-all">
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-6">
                  <div className="flex items-start gap-4 flex-1">
                    {/* Avatar */}
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-teal-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {user.full_name ? user.full_name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                    </div>

                    {/* User Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {user.full_name || 'No name'}
                        </h3>
                        {user.role === 'admin' && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                            <Shield size={12} />
                            Admin
                          </span>
                        )}
                        {user.SPESHELBOI === 'ACCESS_GRANTED' && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                            <Building size={12} />
                            Partner
                          </span>
                        )}
                      </div>

                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Mail size={14} />
                          <span>{user.email}</span>
                        </div>
                        {user.company && (
                          <div className="flex items-center gap-2">
                            <Building size={14} />
                            <span>{user.company}</span>
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-2">
                          Member since {new Date(user.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  {user.role !== 'admin' && (
                    <div>
                      <Button
                        variant={user.SPESHELBOI === 'ACCESS_GRANTED' ? 'outline' : 'default'}
                        size="sm"
                        onClick={() => togglePartnerAccess(user.id, user.SPESHELBOI)}
                        className={user.SPESHELBOI === 'ACCESS_GRANTED' 
                          ? 'border-green-600 text-green-600 hover:bg-green-50'
                          : 'bg-blue-600 hover:bg-blue-700'
                        }
                      >
                        <Building size={16} className="mr-2" />
                        {user.SPESHELBOI === 'ACCESS_GRANTED' ? 'Remove Partner' : 'Grant Partner'}
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Info */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Organization Access:</strong> Users with Organization access can view the Leadership & Partnerships page, which contains confidential team and partnership information.
        </p>
      </div>
    </div>
  );
}
